/**
 * Branch Statistics Component
 * Displays detailed statistics for a branch with minimalistic design
 */

import React, { useMemo } from "react";
import { BranchStatistics as BranchStatsType } from "../types/branch.types";
import Icon from "../../../components/icon/icon";
import "./branch-statistics.css";

interface BranchStatisticsProps {
  statistics: BranchStatsType;
  branchName: string;
}

interface StatisticItem {
  label: string;
  value: number;
  icon: string;
  variant?: "success" | "default";
}

interface StatisticSection {
  title: string;
  icon: string;
  items: StatisticItem[];
}

/**
 * Branch statistics display component with minimalistic neumorphism design
 */
const BranchStatistics: React.FC<BranchStatisticsProps> = ({
  statistics,
  branchName,
}) => {
  const sections: StatisticSection[] = useMemo(
    () => [
      {
        title: "Пользователи",
        icon: "lead",
        items: [
          {
            label: "Всего пользователей",
            value: statistics.totalUsers,
            icon: "explore",
          },
          {
            label: "Активные пользователи",
            value: statistics.activeUsers,
            variant: "success",
            icon: "eye",
          },
        ],
      },
      {
        title: "Бренды",
        icon: "brand",
        items: [
          {
            label: "Всего брендов",
            value: statistics.totalBrands,
            icon: "explore",
          },
          {
            label: "Активные бренды",
            value: statistics.activeBrands,
            variant: "success",
            icon: "eye",
          },
        ],
      },
      {
        title: "Модели",
        icon: "model",
        items: [
          {
            label: "Всего моделей",
            value: statistics.totalModels,
            icon: "explore",
          },
          {
            label: "Активные модели",
            value: statistics.activeModels,
            variant: "success",
            icon: "eye",
          },
        ],
      },
      {
        title: "Автомобили",
        icon: "car",
        items: [
          {
            label: "Всего автомобилей",
            value: statistics.totalCars,
            icon: "explore",
          },
          {
            label: "Активные автомобили",
            value: statistics.activeCars,
            variant: "success",
            icon: "eye",
          },
        ],
      },
    ],
    [statistics]
  );

  return (
    <div className="branch-statistics-enhanced">
      <div className="branch-statistics-enhanced__grid">
        {sections.map((section) => (
          <div key={section.title} className="branch-statistics-enhanced__card">
            <div className="branch-statistics-enhanced__items">
              {section.items.map((item, idx) => (
                <div
                  key={idx}
                  className={`branch-statistics-enhanced__item branch-statistics-enhanced__item--${
                    item.variant || "default"
                  }`}
                >
                  <div className="branch-statistics-enhanced__item-label">
                    {item.icon && (
                      <Icon
                        name={item.icon}
                        size="small"
                        className="branch-statistics-enhanced__item-icon"
                      />
                    )}
                    <span>{item.label}</span>
                  </div>
                  <div className="branch-statistics-enhanced__item-value">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export { BranchStatistics };
